// Stitch a transcript's word-level timestamps against the question
// markers captured during the take, then format the result as a paper
// edit ready to paste into Google Docs / Notion / a Premiere assistant
// edit. Shared with both the worker and the client (the client renders
// it, the worker may one day stash a copy in R2 for editors).

import type {
  DecodedTranscript,
  QuestionMarker,
  Recording,
} from './types';
import type { InterviewQuestion } from './extractInterviewQuestions';

export interface PaperEditSegment {
  /** Question index, or null for the "before any marker was tapped"
   *  preamble that still belongs in the paper edit (mic check, intro,
   *  small talk). */
  questionIndex: number | null;
  questionText: string;
  /** Seconds from the start of the recording. */
  startS: number;
  /** Seconds from the start of the recording. Equals durationS for the
   *  trailing segment when there's no following marker. */
  endS: number;
  /** Concatenated word text for this segment. */
  text: string;
}

export interface PaperEditInput {
  /** Decoded transcript_json — text required, words optional (we fall
   *  back to a single un-timed segment when timestamps are absent). */
  transcript: DecodedTranscript;
  /** Parsed question_markers (post-JSON.parse). Empty array = no
   *  markers were tapped during the take. */
  markers: QuestionMarker[];
  /** The interview questions the script was authored with, in order.
   *  We use these to label segments; an index outside this array
   *  (shouldn't happen but be defensive) falls back to "Untitled". */
  questions: InterviewQuestion[];
  /** Recording metadata used for the document header. */
  recording: Pick<Recording, 'duration_ms' | 'recorded_at'>;
  /** Display title for the document header (script title + client). */
  title: string;
  clientName: string;
  takeNumber: number;
}

/** Splits the transcript into one segment per question marker, plus an
 *  optional preamble segment if anything was said before the first
 *  marker. Pure function — used both for in-app display and for the
 *  Markdown export.
 *
 *  Two segmentation sources are tried in order:
 *    1. `transcript.words` — per-word timestamps. The base Whisper model
 *       returns these; Whisper-large-v3-turbo does not.
 *    2. `transcript.vtt` — WebVTT cues with per-segment timestamps. Turbo
 *       returns these. Cues are usually 3–8 s of audio each, which is
 *       coarser than per-word but more than fine for question markers
 *       that are at least 30 s apart in practice.
 *  Falls back to a single unsegmented block if neither is available.
 */
export function buildPaperEditSegments(
  input: Omit<PaperEditInput, 'title' | 'clientName' | 'takeNumber'>,
): PaperEditSegment[] {
  const { transcript, markers, questions, recording } = input;

  const timed = transcript.words && transcript.words.length > 0
    ? transcript.words.map((w) => ({ start: w.start, text: w.word }))
    : parseVttCues(transcript.vtt);

  const durationS = recording.duration_ms !== null
    ? recording.duration_ms / 1000
    : timed.length > 0
      ? timed[timed.length - 1].start
      : 0;

  // No markers at all → single block, label depends on whether we have
  // timestamps available (cosmetic only).
  if (markers.length === 0) {
    return [
      {
        questionIndex: null,
        questionText: 'Full transcript',
        startS: 0,
        endS: durationS,
        text: transcript.text.trim(),
      },
    ];
  }
  // Markers exist but no timestamps to bucket against → still a single
  // block, but labelled honestly so the UI explains why.
  if (timed.length === 0) {
    return [
      {
        questionIndex: null,
        questionText: 'Unsegmented transcript',
        startS: 0,
        endS: durationS,
        text: transcript.text.trim(),
      },
    ];
  }

  // Markers are stored ascending by time anyway, but stay defensive
  // against a future re-order during editing.
  const sortedMarkers = [...markers].sort((a, b) => a.t - b.t);

  // Build a list of segment ranges (start..end in seconds). Preamble
  // before the first marker is included if it contains words.
  const ranges: Array<{ qIndex: number | null; startS: number; endS: number }> = [];
  if (sortedMarkers[0].t > 0) {
    ranges.push({
      qIndex: null,
      startS: 0,
      endS: sortedMarkers[0].t / 1000,
    });
  }
  for (let i = 0; i < sortedMarkers.length; i++) {
    const start = sortedMarkers[i].t / 1000;
    const end =
      i < sortedMarkers.length - 1
        ? sortedMarkers[i + 1].t / 1000
        : durationS;
    ranges.push({ qIndex: sortedMarkers[i].q, startS: start, endS: end });
  }

  // Bucket timed chunks (words OR VTT cues) into ranges. Each chunk
  // lands in the latest range whose start ≤ chunk.start, so a chunk
  // straddling a marker is attributed to whichever question was active
  // when the speaker started saying it.
  const segments: PaperEditSegment[] = ranges.map((r) => ({
    questionIndex: r.qIndex,
    questionText:
      r.qIndex === null
        ? 'Before first question'
        : questions[r.qIndex]?.text ?? `Question ${r.qIndex + 1}`,
    startS: r.startS,
    endS: r.endS,
    text: '',
  }));

  for (const chunk of timed) {
    let target = 0;
    for (let i = 0; i < ranges.length; i++) {
      if (ranges[i].startS <= chunk.start) target = i;
      else break;
    }
    segments[target].text +=
      (segments[target].text.length > 0 ? ' ' : '') + chunk.text.trim();
  }

  return segments.filter((s) => s.text.length > 0);
}

interface TimedChunk {
  /** Seconds from the start of the recording. */
  start: number;
  text: string;
}

/**
 * Parse a WebVTT string into per-cue timed chunks. We only care about
 * the cue's start time + text, not the end time (which the bucketing
 * logic doesn't use) and not styles/voices. Empty / malformed input
 * returns an empty array.
 *
 * VTT format reminder:
 *   WEBVTT
 *
 *   1
 *   00:00:00.000 --> 00:00:05.200
 *   Hello world.
 *
 *   2
 *   00:00:05.200 --> 00:00:09.800
 *   Second cue text.
 */
function parseVttCues(vtt: string | undefined): TimedChunk[] {
  if (!vtt) return [];
  const lines = vtt.split(/\r?\n/);
  const out: TimedChunk[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Timestamp line: `00:01:24.500 --> 00:01:29.000` (hours optional).
    const m = line.match(
      /^(?:(\d+):)?(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\s*-->/,
    );
    if (!m) continue;
    const hours = m[1] ? Number.parseInt(m[1], 10) : 0;
    const minutes = Number.parseInt(m[2], 10);
    const seconds = Number.parseInt(m[3], 10);
    const ms = m[4] ? Number.parseInt(m[4].padEnd(3, '0'), 10) : 0;
    const start = hours * 3600 + minutes * 60 + seconds + ms / 1000;

    // Collect subsequent non-blank lines until the next blank as the
    // cue text. Strips VTT inline tags like <c.color> defensively.
    const parts: string[] = [];
    let j = i + 1;
    while (j < lines.length && lines[j].trim().length > 0) {
      parts.push(lines[j].replace(/<[^>]+>/g, '').trim());
      j++;
    }
    const text = parts.join(' ').trim();
    if (text) out.push({ start, text });
    i = j;
  }
  return out;
}

/** Render the segments as a Markdown paper edit. */
export function buildPaperEditMarkdown(input: PaperEditInput): string {
  const segments = buildPaperEditSegments(input);
  const date = new Date(input.recording.recorded_at).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const lines: string[] = [];
  lines.push(`# ${input.clientName} — ${input.title}`);
  lines.push('');
  lines.push(
    `**Take ${input.takeNumber}** · Recorded ${date} · ${formatTimecode(
      (input.recording.duration_ms ?? 0) / 1000,
    )}`,
  );
  lines.push('');
  lines.push('---');
  lines.push('');
  for (const seg of segments) {
    if (seg.questionIndex === null) {
      lines.push(`## ${seg.questionText}`);
    } else {
      lines.push(`## Q${seg.questionIndex + 1}: ${seg.questionText}`);
    }
    lines.push(
      `*${formatTimecode(seg.startS)} — ${formatTimecode(seg.endS)}*`,
    );
    lines.push('');
    lines.push(seg.text);
    lines.push('');
  }
  return lines.join('\n').trimEnd() + '\n';
}

/** Format a duration in seconds as mm:ss (or h:mm:ss past an hour). */
export function formatTimecode(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const total = Math.floor(s);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = minutes.toString().padStart(hours > 0 ? 2 : 1, '0');
  const ss = seconds.toString().padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
